<template>
  <section class="py-space-lg py-lg-space-xxl" :class="sectionClass">
    <div class="container px-0 px-lg-gutter">
      <div class="row gx-0">
        <div
          ref="backgroundContainer"
          class="col-12 col-lg-6 description-img sa-lazy"
          :class="bgImageClass"
          :style="bgStyleClass"
          :data-background-image="dataBgImage">
          <div
            class="col-lg-12 px-space-xs py-space-lg py-md-space-md p-lg-space-lg my-lg-space-lg description-div"
            :class="textClass">
            <h2 class="mb-space-sm" :class="titleClass">{{ title }}</h2>
            <div>
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted, ref, watch } from "vue";

interface Props {
  title: string;
  titleVariant?: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  image: string;
  imageLazy?: boolean;
  sectionBackgroundVariant?: string;
  imagePosition?: string;
  textBackgroundVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  titleVariant: "dark-3",
  titleSize: "medium",
  imageLazy: true,
  sectionBackgroundVariant: "white",
  imagePosition: "left",
  textBackgroundVariant: "white",
});

const backgroundContainer = ref<HTMLElement>();
const observer: lozad.Observer | null =
  window && props.imageLazy ? lozad(".sa-lazy") : null;

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const sectionClass = computed(() => {
  return `bg-${props.sectionBackgroundVariant}`;
});

const bgStyleClass = computed(() => {
  if (props.imageLazy === false) {
    return {
      "background-image": `url('${props.image}')`,
    };
  } else {
    return null;
  }
});

const dataBgImage = computed(() => {
  return props.imageLazy ? props.image : null;
});

const bgImageClass = computed(() => {
  return props.imagePosition == "left" ? `image-side-left` : `image-side-right`;
});

const textClass = computed(() => {
  return `bg-${props.textBackgroundVariant}`;
});

watch(
  () => props.image,
  () => {
    if (typeof window !== "undefined" && window !== null && observer !== null) {
      backgroundContainer.value?.removeAttribute("data-loaded");
      observer.triggerLoad(backgroundContainer.value!);
    }
  },
  // triggering post to make sure lozad gets the new attribute value from the dom
  { flush: "post" }
);

onMounted(() => {
  if (typeof window !== "undefined" && window !== null && props.imageLazy) {
    observer !== null ? observer.observe() : null;
  }
});
</script>

<style lang="scss" scoped>
@media (max-width: 991px) {
  .description-img {
    background-image: none !important;
  }
}
/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .description-img {
    background-position: center top;
    background-repeat: no-repeat;
    background-size: cover;
    margin-right: 15px;
    min-height: 350px;

    &.image-side-right {
      margin-left: 43.33333%;
    }
  }

  .description-div {
    .image-side-right & {
      margin-left: -83.3333%;
    }

    .image-side-left & {
      margin-left: 83.3333%;
    }
  }
}

@media (min-width: 1400px) {
  .description-img {
    min-height: 450px;
  }
}
</style>
